import { CharacterSchema, type Character } from '../schemas/character';
import { migrateCharacter } from './characterMigration';

/**
 * Chiave di localStorage per il PG finalizzato attivo.
 *
 * **MVP one-character**: il builder gestisce un solo PG salvato alla volta.
 * Multi-PG arriverà con una refattorizzazione di questa firma in una
 * struttura `dwr.characters.v2: { [id]: Character }` o simile.
 *
 * Versione `v2` allineata a `CHARACTER_SCHEMA_VERSION`. Quando bumpiamo
 * lo schema a v3, anche questa chiave cambia, e `loadCharacter` userà
 * `migrateCharacter` per leggere PG salvati con chiave `v2`.
 */
const CHARACTER_KEY = 'dwr.character.v2';

/**
 * Guard per evitare errori in ambienti privi di `localStorage` (test
 * non-DOM, futuro SSR). I test in jsdom hanno window disponibile, ma il
 * controllo difensivo costa poco e protegge da edge case.
 */
function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

/**
 * Salva il PG finalizzato in localStorage.
 *
 * Sovrascrive l'eventuale PG già presente (MVP single-character). Il caller
 * dovrebbe aver già finalizzato via `finalizeCharacter` (che fa parse Zod);
 * qui ri-validiamo per difesa in profondità contro chiamate dirette.
 */
export function saveCharacter(character: Character): void {
  const storage = getStorage();
  if (!storage) return;
  // Re-validazione: lancia se invariante violato (es. caller ha mutato a mano)
  const validated = CharacterSchema.parse(character);
  storage.setItem(CHARACTER_KEY, JSON.stringify(validated));
}

/**
 * Carica il PG salvato.
 *
 * Restituisce `null` se non c'è alcun PG salvato. Lancia se i dati
 * salvati sono corrotti (JSON invalido) o non parseabili come Character —
 * in quel caso il caller può scegliere se mostrare errore all'utente
 * o pulire la chiave con `clearCharacter`.
 *
 * Usa `migrateCharacter` come ingresso per supportare future migration:
 * un PG salvato con `schemaVersion: 1` verrebbe migrato; oggi solo v2 esiste.
 */
export function loadCharacter(): Character | null {
  const storage = getStorage();
  if (!storage) return null;
  const raw = storage.getItem(CHARACTER_KEY);
  if (raw === null) return null;
  const parsed: unknown = JSON.parse(raw);
  return migrateCharacter(parsed);
}

/**
 * Cancella il PG salvato (utile dopo finalize per resettare il builder, o
 * per il bottone "Crea nuovo PG" della scheda di view).
 */
export function clearCharacter(): void {
  const storage = getStorage();
  if (!storage) return;
  storage.removeItem(CHARACTER_KEY);
}

/**
 * Serializza un Character come stringa JSON pretty-printed per export
 * (download .json, copy-paste nei salvataggi del giocatore).
 *
 * **Indentazione 2 spazi**: scelta consapevole per leggibilità del file
 * salvato (il giocatore può aprirlo in un editor e ispezionarlo). Costo
 * trascurabile in dimensioni dato che i Character sono piccoli (qualche KB).
 */
export function exportCharacterAsJson(character: Character): string {
  return JSON.stringify(character, null, 2);
}

/**
 * Deserializza un Character da stringa JSON e lo valida.
 *
 * Lancia se:
 *  - il JSON è malformato (stringa non parseabile)
 *  - il contenuto non rispetta `CharacterSchema` (campi mancanti, tipi
 *    sbagliati, schemaVersion sconosciuta)
 *
 * Usa `migrateCharacter` per supportare PG esportati con schema versions
 * precedenti (oggi solo v2 esiste).
 */
export function importCharacterFromJson(json: string): Character {
  const parsed: unknown = JSON.parse(json);
  return migrateCharacter(parsed);
}
